import { useTranslation } from "@outschool/localization";
import React from "react";

const MAX_COLLECTION_NAME_LENGTH = 256;

/**
 * These are collection names used in various places internally and so shouldn't be allowed
 */
const RESTRICTED_COLLECTION_NAMES = [
  "Favorite classes",
  "Learner profile",
  "All",
  "Favorites",
  "All saved classes"
];

/**
 * A hook which contains the validation logic for collection names
 */
export default function useEditableCollectionName(
  savedClassLists: Array<{ name: string }>,
  initialCollectionName?: string
) {
  const { t } = useTranslation(
    "client\\components\\saving\\useEditableCollectionName"
  );
  const [collectionName, setCollectionName] = React.useState<
    string | undefined
  >(initialCollectionName);
  const [errorMessage, setErrorMessage] = React.useState<string | undefined>(
    undefined
  );

  const setCollectionNameWrapped: React.ChangeEventHandler<HTMLInputElement> =
    React.useCallback(
      e => {
        const newValue = e.target.value;
        setCollectionName(newValue);
        const trimmedNewValue = newValue?.trim();
        if (RESTRICTED_COLLECTION_NAMES.includes(trimmedNewValue)) {
          setErrorMessage(
            t`We’re unable to save your list with this name. Please choose another name.`
          );
        } else if (
          savedClassLists?.some(({ name }) => name === trimmedNewValue)
        ) {
          setErrorMessage(
            t('You already have a list called "{{collectionName}}."', {
              collectionName: trimmedNewValue
            })
          );
        } else if (trimmedNewValue.length >= MAX_COLLECTION_NAME_LENGTH) {
          setErrorMessage(
            t("List names must be fewer than {{maxLength}} characters.", {
              maxLength: MAX_COLLECTION_NAME_LENGTH
            })
          );
        } else {
          setErrorMessage(undefined);
        }
      },
      [savedClassLists, t]
    );

  return {
    collectionName,
    errorMessage,
    setCollectionName: setCollectionNameWrapped,
    isCollectionNameValid:
      !errorMessage &&
      !!collectionName?.length &&
      collectionName?.trim() !== initialCollectionName
  };
}
